import type { IndustryPage } from "@/lib/data";

export const hvac: IndustryPage = {
  slug: "hvac",
  seoTitle:
    "Gas Engineer Website Design | Heating & HVAC, Fixed £1,000",
  seoDescription:
    "Gas engineer website design: boiler, heat pump and breakdown pages, your Gas Safe number and local SEO. Fixed £1,000, live in 7 days. Book a free call.",
  serviceName: "Website design for heating engineers and HVAC firms",
  tldr:
    "ConstructionWebDesign.uk offers gas engineer website design for heating engineers and HVAC firms across the UK: boiler, heat pump, breakdown and maintenance contract pages, plus the Gas Safe, F-Gas or MCS details you hold. Each site costs £1,000, a one-off fixed price with no subscription. Your website is live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Gas engineer website design · UK",
    headline: "Website design for heating engineers and HVAC firms, boilers to VRF.",
    highlight: "boilers to VRF",
    sub: "Websites for UK gas engineers, heating engineers and HVAC firms with the Gas Safe, F-Gas and MCS details you hold, boiler and heat pump pages, a 24/7 breakdown route and maintenance contract pages.",
    secondaryCta: { label: "See HVAC demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/plumbing_category.jpg",
      alt: "Heating engineer working on copper pipework and fittings",
      headlineTop: "Win maintenance",
      headlineBottom: "contracts, not callouts.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/trunkline.jpg",
    },
  ],
  demoSlugs: ["trunkline", "brassline", "voltline"],
  demosHeading: "Heating and HVAC demo templates.",
  demosSub:
    "The closest demo templates in the library: new-build plumbing, plumbing and heating, and electrical. Open one to see how a services list, credentials strip and emergency route sit together. These are demos, not client sites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a heating engineer need a website?",
    intro:
      "A heating engineer needs a website because it is where homeowners and facilities managers check your registrations, the systems you work on and how fast you answer a breakdown before they call. Breakdowns and boiler jobs pay the wages, maintenance contracts steady the business, and a heating or HVAC website should make the case for both.",
    points: [
      {
        title: "Sell the contract, not just the callout.",
        body: "Planned preventative maintenance is set out properly: visit frequency, what gets checked, response times, holiday cover and what a breakdown costs a contract customer compared with a non-contract one. A facilities manager can see what they get before they ring.",
        icon: "Wind",
      },
      {
        title: "Lead with Gas Safe, F-Gas and MCS.",
        body: "Gas Safe registration for gas work, F-Gas certification through Refcom or another certification body, MCS for heat pumps, plus BESA or CIBSE membership and manufacturer accreditations. Facilities managers check these before they call, because their own compliance depends on it. Only the ones you hold go on the site.",
        icon: "Shield",
      },
      {
        title: "Answer the breakdown at 3pm on a Friday.",
        body: "A failed chiller in a server room, dead heating in a care home or a boiler down in January is a same-hour decision. Your out-of-hours number, the area you cover and an honest response window belong at the top of the screen, not on a contact page.",
        icon: "Phone",
      },
      {
        title: "Split commercial and domestic work.",
        body: "A homeowner pricing a heat pump under the Boiler Upgrade Scheme and a facilities manager tendering a multi-year maintenance contract want different things. Two routes, two sets of pages and two sets of search terms keep each one on the right path.",
        icon: "Buildings",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What should a gas engineer or HVAC website include?",
    intro:
      "A gas engineer or HVAC website should include the registrations you hold, a page for each service or system, a fast breakdown route and clear maintenance contract details. Websites for heating engineers from ConstructionWebDesign.uk are planned around the parts below.",
    items: [
      {
        title: "Boiler and gas service pages.",
        body: "Boiler installation and replacement, annual boiler servicing, breakdown repairs, landlord gas safety records (CP12), central heating, powerflushing and underfloor heating each get their own page. A homeowner searching for a boiler service lands on your boiler service page, not a general list.",
      },
      {
        title: "A page per commercial system type.",
        body: "Split and multi-split air conditioning, VRF and VRV, chillers, AHUs and ventilation, commercial boilers and plant rooms, LEV testing and duct cleaning. Each system gets its own page and targets its own search.",
      },
      {
        title: "Credentials strip with registration numbers.",
        body: "Gas Safe, F-Gas, MCS, BESA, CIBSE and NICEIC where you hold them, plus manufacturer accreditations, sit in the header, linked to the official register. Gas Safe registration is the legal register of gas engineers in the UK, and a linked number lets a customer check yours in one tap. No accreditation, no badge: anything you don't hold stays off the site.",
      },
      {
        title: "Heat pump and grant pages.",
        body: "Air source and ground source heat pumps, MCS certification and the Boiler Upgrade Scheme explained in plain English, plus a suitability checker that asks for property type, current heating and EPC band. You get the details to judge the job before you call back.",
      },
      {
        title: "24/7 breakdown route.",
        body: "An emergency number that stays on screen on mobile, a coverage map, honest response windows for contract and non-contract customers, and a fault form that asks for site, plant type and access details, so the engineer arrives with the right parts.",
      },
      {
        title: "Maintenance contract pages with real detail.",
        body: "Service levels side by side: visit frequency, plant covered, response times, the parts and labour position and out-of-hours cover. A facilities manager can shortlist you straight from the page without booking a meeting.",
      },
      {
        title: "Local and commercial SEO foundation.",
        body: "On-page SEO with schema markup, a page per system and service, sector pages for offices, retail, data rooms, care homes and industrial units, and town and regional pages for the areas your engineers cover.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a heating engineer website cost in the UK?",
      a: "A heating engineer or HVAC website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. Typical UK ranges for comparison: about £4,000–£5,000 for a freelancer billing £50–£120 an hour, £5,000–£25,000 for an agency, or about £22 a month for Wix or Squarespace. The client owns the website.",
    },
    {
      q: "How long does it take to build a gas engineer website?",
      a: "A gas engineer or HVAC website from ConstructionWebDesign.uk goes live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. On the discovery call you explain the systems you work on, the areas you cover and whether you want more breakdown, installation or contract work.",
    },
    {
      q: "What pages does a plumbing and heating website need?",
      a: "A plumbing and heating website needs a page for each main service (boiler installs, servicing, breakdown repairs, landlord gas safety records, heat pumps), the areas you cover, a breakdown contact route and a short fault or enquiry form. Gas Safe details belong near the top. Firms doing commercial HVAC work also need system-type pages and maintenance contract pages with response times.",
    },
    {
      q: "Do heating engineers need a website?",
      a: "Yes, heating engineers need a website, whether they work alone or run a team. A website is where homeowners and facilities managers check your Gas Safe or F-Gas details, the systems you cover, your breakdown number and your contract terms before they call. Directory profiles list you next to other firms. Your own website shows only your business, takes enquiries while you're on a job and belongs to you.",
    },
    {
      q: "Should a heating engineer show Gas Safe, F-Gas and MCS details on the website?",
      a: "Yes, a heating engineer should show every registration they hold near the top of the website. Gas Safe registration is the legal register of gas engineers in the UK. F-Gas certification is a legal requirement for work on the refrigerant circuit of air conditioning and heat pump systems. MCS certification lets an installer apply for Boiler Upgrade Scheme grants in England and Wales. ConstructionWebDesign.uk links each badge to its official register.",
    },
    {
      q: "How do HVAC firms get more maintenance contracts from a website?",
      a: "HVAC firms turn website visitors into maintenance contract enquiries by publishing what a contract includes and what it costs to be without one. A maintenance page that sets out visit frequency, plant covered, response times, out-of-hours cover and the priority contract customers get lets a facilities manager shortlist you without a meeting. The same page gives a one-off breakdown customer a reason to sign up.",
    },
    {
      q: "Should a heating engineer's website have a heat pump section?",
      a: "Yes, a heating engineer who installs heat pumps should give them their own section of the website. Heat pump buyers research differently from boiler customers: they read about MCS certification, the Boiler Upgrade Scheme, running costs and radiator sizing, often before they contact anyone. A heat pump section with a suitability checker answers those questions and asks for property type, current heating and EPC band.",
    },
    {
      q: "How does a gas engineer get found on Google?",
      a: "A gas engineer gets found on Google through a website with a page for each service and area, on-page local SEO and a complete Google Business Profile. HVAC website design from ConstructionWebDesign.uk includes on-page SEO: schema markup, a page per system or service, sector pages and regional targeting for the areas your engineers cover. Nobody can honestly guarantee a ranking, but these are the basics a heating firm needs in place.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/how-to-win-subcontractor-work",
      label: "How to win subcontractor work and get on tender lists",
    },
    {
      href: "/blog/chas-accreditation-explained",
      label: "CHAS accreditation explained: cost, process, worth it?",
    },
  ],
  finalCta: {
    headline: "Talk to us about your heating or HVAC website.",
    highlight: "heating or HVAC website",
    sub: "Book a free 30-minute call or email hello@constructionwebdesign.uk. Fixed £1,000, one-off, and your website is live in 7 days.",
  },
};
